import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';

interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export class MaxRectsStrategy implements CuttingStrategy {
    name = 'Max Rects';

    optimize(material: IMaterial, parts: IMaterial[]): ICutResult {
        // Sort parts by area (largest first)
        const sortedParts = [...parts].sort((a, b) => (b.width * b.height) - (a.width * a.height));
        const usedMaterials: IMaterial[] = [];
        const remainingParts = [...sortedParts];

        while (remainingParts.length > 0) {
            const currentMaterial: IMaterial = {
                ...material,
                id: `material-${usedMaterials.length + 1}`,
                children: [] as IMaterial[]
            };

            // Initialize with one large free rectangle
            const freeRectangles: Rectangle[] = [
                { x: 0, y: 0, width: material.width, height: material.height }
            ];

            let placed = false;
            do {
                placed = false;

                // Try to place each remaining part
                for (let i = 0; i < remainingParts.length; i++) {
                    const part = remainingParts[i];

                    // Find best position
                    const position = this.findBestPosition(part, freeRectangles);

                    if (position) {
                        // Place the part
                        const placedPart: IMaterial = {
                            ...part,
                            x: position.x,
                            y: position.y
                        };

                        currentMaterial.children!.push(placedPart);

                        // Update free rectangles
                        this.splitFreeRectangles(freeRectangles, position);

                        // Remove part from remaining list
                        remainingParts.splice(i, 1);
                        placed = true;
                        break;
                    }
                }
            } while (placed);

            // If we placed at least one part
            if (currentMaterial.children!.length > 0) {
                usedMaterials.push(currentMaterial);
            } else {
                // If we couldn't place any parts, break to avoid infinite loop
                break;
            }
        }

        return {
            usedMaterials,
            wastePercentage: this.calculateWaste(material, usedMaterials),
            strategyName: this.name
        };
    }

    private findBestPosition(part: IMaterial, freeRectangles: Rectangle[]): Rectangle | null {
        let bestScore = Infinity;
        let bestPosition: Rectangle | null = null;

        for (const rect of freeRectangles) {
            // Check if part fits
            if (rect.width >= part.width && rect.height >= part.height) {
                // Score using "best short side fit"
                const score = Math.min(
                    rect.width - part.width,
                    rect.height - part.height
                );

                if (score < bestScore) {
                    bestScore = score;
                    bestPosition = {
                        x: rect.x,
                        y: rect.y,
                        width: part.width,
                        height: part.height
                    };
                }
            }
        }

        return bestPosition;
    }

    private splitFreeRectangles(freeRectangles: Rectangle[], placed: Rectangle): void {
        for (let i = freeRectangles.length - 1; i >= 0; i--) {
            const rect = freeRectangles[i];

            // Check if placed rectangle intersects with this free rectangle
            if (this.isIntersecting(placed, rect)) {
                freeRectangles.splice(i, 1);

                // Add new free rectangles based on the split
                // Right split
                if (placed.x + placed.width < rect.x + rect.width) {
                    freeRectangles.push({
                        x: placed.x + placed.width,
                        y: rect.y,
                        width: rect.width - (placed.x + placed.width - rect.x),
                        height: rect.height
                    });
                }

                // Bottom split
                if (placed.y + placed.height < rect.y + rect.height) {
                    freeRectangles.push({
                        x: rect.x,
                        y: placed.y + placed.height,
                        width: rect.width,
                        height: rect.height - (placed.y + placed.height - rect.y)
                    });
                }
            }
        }

        // Prune rectangles that are contained within others
        this.pruneFreeRectangles(freeRectangles);
    }

    private isIntersecting(a: Rectangle, b: Rectangle): boolean {
        return !(
            a.x >= b.x + b.width ||
            a.y >= b.y + b.height ||
            a.x + a.width <= b.x ||
            a.y + a.height <= b.y
        );
    }

    private pruneFreeRectangles(freeRectangles: Rectangle[]): void {
        for (let i = freeRectangles.length - 1; i >= 0; i--) {
            for (let j = freeRectangles.length - 1; j >= 0; j--) {
                if (i !== j && this.isContainedIn(freeRectangles[i], freeRectangles[j])) {
                    freeRectangles.splice(i, 1);
                    break;
                }
            }
        }
    }

    private isContainedIn(a: Rectangle, b: Rectangle): boolean {
        return (
            a.x >= b.x &&
            a.y >= b.y &&
            a.x + a.width <= b.x + b.width &&
            a.y + a.height <= b.y + b.height
        );
    }

    private calculateWaste(originalMaterial: IMaterial, usedMaterials: IMaterial[]): number {
        const totalArea = originalMaterial.width * originalMaterial.height * usedMaterials.length;
        let usedArea = 0;

        usedMaterials.forEach(material => {
            if (material.children) {
                material.children.forEach(part => {
                    usedArea += part.width * part.height;
                });
            }
        });

        return ((totalArea - usedArea) / totalArea) * 100;
    }
}
